import PropTypes from 'prop-types';
import {
  Icon, IconColors, IconKeys, iconRenderer,
} from '@components/icon';
import { FeaturedIcon, FeaturedIconColors, FeaturedIconSizes } from '@leegality/leegality-react-component-library';
import { toast } from 'react-hot-toast';

const DEFAULT_DURATION = 4000;
const NotificationTypes = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  PRIMARY: 'primary',
  CUSTOM: 'custom',
};

const Notification = ({
  thisToast, type, text, supportingText, showClose, showIcon, customView, bodyClassNames,
}) => {
  const getIcon = () => {
    switch (type) {
      case NotificationTypes.ERROR:
        return (
          <FeaturedIcon
            color={FeaturedIconColors.ERROR}
            size={FeaturedIconSizes.MEDIUM}
            renderIcon={() => iconRenderer({ iconName: IconKeys.AlertCircle, color: IconColors.ERROR })}
          />
        );
      case NotificationTypes.SUCCESS:
        return (
          <FeaturedIcon
            color={FeaturedIconColors.SUCCESS}
            size={FeaturedIconSizes.MEDIUM}
            renderIcon={() => iconRenderer({ iconName: IconKeys.CheckCircle, color: IconColors.SUCCESS })}
          />
        );
      case NotificationTypes.WARNING:
        return (
          <FeaturedIcon
            color={FeaturedIconColors.WARNING}
            size={FeaturedIconSizes.MEDIUM}
            renderIcon={() => iconRenderer({ iconName: IconKeys.AlertTriangle, color: IconColors.WARNING })}
          />
        );
      case NotificationTypes.CUSTOM:
        return (
          <FeaturedIcon
            color={FeaturedIconColors.SUCCESS}
            size={FeaturedIconSizes.MEDIUM}
            renderIcon={() => iconRenderer({ iconName: IconKeys.CheckCircle, color: IconColors.SUCCESS })}
          />
        );
      default: return null;
    }
  };

  const getBody = () => {
    switch (type) {
      case NotificationTypes.ERROR:
      case NotificationTypes.WARNING:
      case NotificationTypes.SUCCESS:
        return (
          <div className={bodyClassNames}>
            <div className="noti-text">{text}</div>
            <div className="noti-supporting-text">{supportingText}</div>
          </div>
        );
      case NotificationTypes.CUSTOM:
        return (
          <div className="noti-body">
            {customView}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`noti-cont ${type}`}>
      <div className="flex items-center gap-4">
        {showIcon && getIcon()}
        {getBody()}
      </div>

      {showClose ? (
        <Icon
          className="noti-close-icon"
          icon={iconRenderer({ iconName: IconKeys.X, color: IconColors.GREY })}
          size={FeaturedIconSizes.MEDIUM}
          color={IconColors.GREY}
          onClick={() => toast.dismiss(thisToast.id)}
        />
      ) : null}
    </div>
  );
};

Notification.propTypes = {
  thisToast: PropTypes.shape({ id: PropTypes.string }),
  type: PropTypes.oneOf(Object.values(NotificationTypes)),
  text: PropTypes.string,
  supportingText: PropTypes.string,
  showClose: PropTypes.bool,
  showIcon: PropTypes.bool,
  customView: PropTypes.node,
  bodyClassNames: PropTypes.string,
};

Notification.defaultProps = {
  thisToast: {},
  type: NotificationTypes.SUCCESS,
  text: '',
  supportingText: '',
  showClose: true,
  showIcon: true,
  customView: null,
  bodyClassNames: '',
};

const commonToastProperties = {
  duration: 4000,
  position: 'top-right',
};

const notification = {
  success: (text, supportingText, showIcon) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.SUCCESS}
      showIcon={showIcon}
    />
  ), {
    ...commonToastProperties,
  }),
  error: (text, supportingText, showIcon) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.ERROR}
      showIcon={showIcon}
    />
  ), {
    ...commonToastProperties,
  }),
  warning: (text, supportingText, showIcon) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.WARNING}
      showIcon={showIcon}
    />
  ), {
    ...commonToastProperties,
  }),
  custom: (view, duration, showClose, showIcon) => toast((t) => (
    <Notification
      thisToast={t}
      customView={view}
      type={NotificationTypes.CUSTOM}
      showClose={showClose}
      showIcon={showIcon}
    />
  ), {
    ...commonToastProperties,
    className: 'noti-toast noti-custom',
    duration: duration || DEFAULT_DURATION,
  }),
  dismiss: (id) => toast.dismiss(id),
  remove: (id) => toast.remove(id),

};

export default notification;
